import React from 'react';
import { Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { tokens } from '../../theme/tokens';
import { extractApiErrorMessage, notifications } from '../../utils';
import { SettingsService } from '../../hooks/SettingsService';
import { ApiService } from '../../hooks';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { Title, LegacyGrid as Grid } from '../../elements';
import { Skeleton } from '../../elements/ui/skeleton';

import { AnimalCapacityViewModel } from '../../models/settings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { InfoOutlined } from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { getDashboardConfigByFarmType } from '../../utils';
import useAuthContext from '../../hooks/useAuthContext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';

const AnimalCapacity: React.FC<any> = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const settingsService = new SettingsService();
  const apiService = new ApiService();
  const confirmDialog = useConfirmation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [animalCapacity, setAnimalCapacity] = React.useState({} as AnimalCapacityViewModel);
  const [fromDate, setFromDate] = React.useState<Date | null>(null);
  const [toDate, setToDate] = React.useState<Date | null>(null);

  const loadAnimalCapacity = async (from: Date | null, to: Date | null) => {
    setIsBusy(true);

    settingsService
      .getAnimalCapacity(from ? format(from, 'yyyy-MM-dd') : undefined, to ? format(to, 'yyyy-MM-dd') : undefined)
      .then((capacity) => {
        setAnimalCapacity(capacity);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadAnimalCapacity(fromDate, toDate);

      loadData();
    }
  }, [loaded]);

  const handleFromDateChange = (value: unknown) => {
    const newDate = value as Date | null;

    setFromDate(newDate);
    loadAnimalCapacity(newDate, toDate);
  };

  const handleToDateChange = (value: unknown) => {
    const newDate = value as Date | null;

    setToDate(newDate);
    loadAnimalCapacity(fromDate, newDate);
  };

  const handleExport = () => {
    if (!fromDate || !toDate) {
      return;
    }

    confirmDialog({
      dialogId: 'livestock-balance-card-export-dialog',
      title: t('f-export'),
      message: t('f-export-livestock-info'),
      alertSeverity: 'info'
    }).then(() => {
      apiService.api.documents
        .documentsCreateLivestockBalanceCardExport({
          startDate: format(fromDate, 'yyyy-MM-dd'),
          endDate: format(toDate, 'yyyy-MM-dd')
        })
        .then((result) => {
          if (result) {
            notifications.success(t('f-export-ok'));
          } else {
            notifications.warning(t('f-export-error', { error: '' }));
          }
        })
        .catch((e) => {
          notifications.error(t('f-export-error', { error: e.toString() }));
        });
    });
  };

  const renderCapacityRow = (name: string, value: number) => {
    return (
      <TableRow>
        <TableCell variant="head" align="right">
          {name}
        </TableCell>
        <TableCell>{value}</TableCell>
      </TableRow>
    );
  };

  const renderTableBody = () => {
    const config = getDashboardConfigByFarmType(state.farmType);

    return (
      <TableBody>
        {config.displaySow && renderCapacityRow(t('Sow'), animalCapacity.sow)}

        {config.displayGilt && renderCapacityRow(t('Gilt'), animalCapacity.gilt)}

        {config.displayPiglet && renderCapacityRow(t('Piglet'), animalCapacity.piglet)}

        {config.displayFattening && renderCapacityRow(t('Fattening'), animalCapacity.fattening)}

        {config.displayCow && renderCapacityRow(t('Cow'), animalCapacity.cow)}

        {config.displayGoat && renderCapacityRow(t('Goat'), animalCapacity.goat)}

        {config.displayCalf && renderCapacityRow(t('Calf'), animalCapacity.calf)}
      </TableBody>
    );
  };

  const renderTableSkeleton = () => {
    const config = getDashboardConfigByFarmType(state.farmType);
    const rowCount = [
      config.displaySow,
      config.displayGilt,
      config.displayPiglet,
      config.displayFattening,
      config.displayCow,
      config.displayGoat,
      config.displayCalf
    ].filter(Boolean).length;

    return (
      <TableBody>
        {Array.from({ length: rowCount }).map((_, index) => (
          <TableRow key={index}>
            <TableCell align="right">
              <Skeleton className="h-4 w-20 ml-auto" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-12" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item container xs={10} spacing={1}>
        <Grid item>
          <AccountTreeIcon color="primary" fontSize="large" />
        </Grid>
        <Grid item>
          <Title>{t('f-farm-anim-cap')}</Title>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container alignItems="center" spacing={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid item>
              <DatePicker
                format="dd/MM/yyyy"
                label={t('f-com-from')}
                value={fromDate}
                onChange={handleFromDateChange}
                maxDate={toDate ?? new Date()}
                slotProps={{
                  textField: {
                    size: 'small'
                  },
                  field: {
                    clearable: true
                  }
                }}
              />
            </Grid>
            <Grid item>
              <DatePicker
                format="dd/MM/yyyy"
                label={t('f-com-to')}
                value={toDate}
                onChange={handleToDateChange}
                minDate={fromDate ?? undefined}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    size: 'small'
                  },
                  field: {
                    clearable: true
                  }
                }}
              />
            </Grid>
          </LocalizationProvider>
          <Grid item>
            <Tooltip arrow title={t('f-farm-anim-cap-date-tooltip')}>
              <InfoOutlined color="primary" fontSize="small" />
            </Tooltip>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!fromDate || !toDate}
              onClick={handleExport}
            >
              {t('f-export')}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {(isBusy || loaded) && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item md={5} xs={12}>
              <Paper>
                <Table aria-label="animal capacity data">
                  <TableHead sx={{ background: tokens.sidebar }}>
                    <TableRow>
                      <TableCell align="right">{t('f-com-animal-type')}</TableCell>
                      <TableCell>{t('f-farm-anim-count')}</TableCell>
                    </TableRow>
                  </TableHead>

                  {isBusy ? renderTableSkeleton() : renderTableBody()}
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default AnimalCapacity;
